import React, { useCallback } from 'react';
import { Body1, Icon, type IconProps, Tooltip } from 'venn-ui-kit';
import { toClassName, useModuleLazily } from 'venn-utils';
import type { DropResult } from '@hello-pangea/dnd';
import type ReactDnd from '@hello-pangea/dnd';
import { isEmpty } from 'lodash';
import type { MetricSpecificError } from 'venn-state';
import { cn } from '@/lib/utils';

export interface ReorderableItem {
  key: string;
  /** Label is used if itemRenderer is not defined. */
  label?: string;
  tooltipContent?: string;
  errorMessage?: MetricSpecificError;
}

export interface ReorderableListProps<ITEM = ReorderableItem> {
  reorderableItems: ITEM[];
  onReorderItems: (newItems: ITEM[]) => void;
  /** Custom item component which will be used instead of label. */
  itemRenderer?: (item: ITEM, index: number) => JSX.Element;
  hideDelete?: boolean;
  disabled?: boolean;
  id?: string;
  /** The size of the margin around the list  */
  margin?: number;
  counterMargin?: number;
  forceDefaultMargin?: boolean;
  /** If not provided, deletion will call onReorderItems with the deleted item removed. */
  onDeleteItem?: (deletedKey: string) => void;
}

export interface ReorderableListInnerProps<ITEM = ReorderableItem> extends ReorderableListProps<ITEM> {
  reactDnd: typeof ReactDnd;
}

export const REORDERABLE_LIST_HOVER_CLASS = 'reorderable-hover';

/**
 * Inner implementation of a reorderable list, not wrapped by a drag drop context
 * Not to be used outside of ReorderableList or ReorderableListGroup
 */
export const ReorderableListInner = <ITEM extends ReorderableItem>({
  reorderableItems,
  onReorderItems,
  itemRenderer,
  hideDelete,
  disabled,
  id,
  reactDnd,
  margin = 10,
  counterMargin = 30,
  forceDefaultMargin,
  onDeleteItem = (key: string) => {
    onReorderItems(reorderableItems.filter((b) => b.key !== key));
  },
}: ReorderableListInnerProps<ITEM>) => {
  if (reactDnd === null) {
    return null;
  }

  const { Draggable, Droppable } = reactDnd;
  return (
    <Droppable droppableId={id ?? 'droppable'}>
      {(provided) => (
        <ul
          {...provided.droppableProps}
          ref={provided.innerRef}
          className="qa-chosen-templates list-none p-0 my-0"
          style={!forceDefaultMargin ? { margin: `0 -${counterMargin}px` } : undefined}
        >
          {reorderableItems.map((item, index) => (
            <Draggable
              key={item.key}
              draggableId={`${id}|${item.key}|${index}`}
              index={index}
              isDragDisabled={disabled}
            >
              {(draggableProvided, snapshot) => (
                <div
                  ref={draggableProvided.innerRef}
                  {...draggableProvided.draggableProps}
                  {...draggableProvided.dragHandleProps}
                  className={cn(
                    'relative flex items-center w-full hover:rounded-[2px] hover:outline-1 hover:outline hover:outline-venn-grey-30',
                    disabled
                      ? 'hover:bg-transparent hover:cursor-[inherit]'
                      : 'hover:bg-venn-grey-10 hover:cursor-grab',
                    '[&:not(:hover)_.reorderable-hover]:invisible',
                    snapshot.isDragging &&
                      'bg-venn-primary-main inline-flex rounded-[4px] [.analysis-block-content]:m-0',
                    snapshot.isDragging && '[.analysis-block-content]:pl-[17.5px] [.analysis-block-content]:border-l-0',
                    snapshot.isDragging && '[.body-1]:text-venn-white',
                    item.key ? toClassName(item.key) : 'qa-undefined',
                  )}
                >
                  <div className="flex items-center justify-center shrink-0" style={{ width: `${margin}px` }}>
                    {!disabled && (
                      <Icon
                        className={cn('text-[12px] text-venn-dark-blue', REORDERABLE_LIST_HOVER_CLASS)}
                        type="arrows-up-down"
                      />
                    )}
                  </div>
                  {itemRenderer ? itemRenderer(item, index) : <ReorderableListDefaultItemRenderer item={item} />}
                  {!hideDelete && !disabled ? (
                    <button onClick={() => onDeleteItem(item.key)} type="button">
                      <Icon
                        type="trash"
                        className="text-[12px] text-venn-grey-40 hover:text-venn-primary-dark hover:cursor-pointer"
                      />
                    </button>
                  ) : null}
                  <div className="flex items-center justify-center" style={{ width: `${margin}px` }} />
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </ul>
      )}
    </Droppable>
  );
};

export const ReorderableListDefaultItemRenderer = <T extends ReorderableItem>({ item }: { item: T }) => {
  const hasError = !isEmpty(item.errorMessage) && item.errorMessage?.type !== 'warning';
  return (
    <span style={{ flex: 1 }}>
      <li
        key={item.key}
        className={cn(
          'analysis-block-content border-0 border-l-[3px] border-solid border-l-venn-grey-40 pl-[15px] flex-1',
          hasError && 'text-venn-error [&>div]:text-venn-error',
        )}
      >
        <Body1 className="body-1">
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {!isEmpty(item.errorMessage) &&
              (item.errorMessage?.type === 'warning' ? <ReorderableItemWarningIcon /> : <ReorderableItemErrorIcon />)}
            {item.label}
            {item.tooltipContent && (
              <Tooltip content={item.tooltipContent} usePortal>
                <Icon type="info-circle" prefix="far" className="text-venn-grey-60" />
              </Tooltip>
            )}
          </div>
        </Body1>
      </li>
    </span>
  );
};

/**
 * A single reorderable list, where items can be reordered within the list
 */
export const ReorderableList = <ITEM extends ReorderableItem>(props: ReorderableListProps<ITEM>) => {
  const reactDnd: typeof ReactDnd | null = useModuleLazily(useCallback(() => import('@hello-pangea/dnd'), []));

  if (reactDnd === null) {
    return null;
  }

  const { reorderableItems, onReorderItems } = props;

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newBlocksOrder = reorder(reorderableItems, result.source.index, result.destination.index);

    onReorderItems(newBlocksOrder);
  };

  const { DragDropContext } = reactDnd;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <ReorderableListInner {...props} reactDnd={reactDnd} />
    </DragDropContext>
  );
};

export const ReorderableItemErrorIcon = ({ className }: IconProps) => (
  <Icon type="triangle-exclamation" className={cn('text-venn-error text-[12px]', className)} />
);

export const ReorderableItemWarningIcon = ({ className }: IconProps) => (
  <Icon type="diamond-exclamation" className={cn('text-venn-orange text-[12px]', className)} />
);

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const removed = result.splice(startIndex, 1)[0]!;
  result.splice(endIndex, 0, removed);

  return result;
}
